import { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Send, Sparkles, Loader2 } from 'lucide-react';
import { type ChatMessage, initiateConversation, sendMessage, loadChatHistory, saveChatHistory, clearChatHistory } from '../utils/aiAgent';
import { FeedbackBar } from './FeedbackBar';
interface Props {
  onError: (msg: string) => void;
}
const STORAGE_KEY = 'ai-chat-wf';
function uid(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}
function loadWfHistory(): {
  conversationId: string | null;
  messages: ChatMessage[];
} {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {
      conversationId: null,
      messages: []
    };
    const parsed = JSON.parse(raw);
    return {
      conversationId: parsed.conversationId || null,
      messages: Array.isArray(parsed.messages) ? parsed.messages : []
    };
  } catch {
    return {
      conversationId: null,
      messages: []
    };
  }
}
function saveWfHistory(conversationId: string | null, messages: ChatMessage[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      conversationId,
      messages
    }));
  } catch {/* quota exceeded — ignore */}
}
function clearWfHistory(): void {
  localStorage.removeItem(STORAGE_KEY);
}
const WORKFRONT_SUGGESTIONS = ['Find opportunity related to Granger Hertzog', 'Find all in progress requests assigned to XYZ  .', 'Recent updates on "XYZ" request', 'Find gnarr of XYZ'];
export function WorkfrontAiChat({
  onError
}: Props) {
  const stored = loadWfHistory();
  const [messages, setMessages] = useState<ChatMessage[]>(stored.messages);
  const [conversationId, setConversationId] = useState<string | null>(stored.conversationId);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [isInitializing, setIsInitializing] = useState(!stored.conversationId && stored.messages.length === 0);
  const [error, setError] = useState<string | null>(null);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const streamingMsgIdRef = useRef<string | null>(null);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages]);
  useEffect(() => {
    saveWfHistory(conversationId, messages);
  }, [conversationId, messages]);
  useEffect(() => {
    if (conversationId || messages.length > 0) {
      setIsInitializing(false);
      return;
    }
    const controller = new AbortController();
    setIsInitializing(true);
    initiateConversation(evt => {
      if (evt.event === 'init') return;
      try {
        const parsed = JSON.parse(evt.data);
        if (parsed.message) {
          const greeting: ChatMessage = {
            id: uid(),
            role: 'assistant',
            content: parsed.message,
            timestamp: Date.now()
          };
          setMessages([greeting]);
        }
      } catch {/* ignore parse errors */}
    }, controller.signal).then(id => {
      if (id) setConversationId(id);
    }).catch(err => {
      if (err.name !== 'AbortError') {
        setError('Failed to connect to AI assistant.');
      }
    }).finally(() => setIsInitializing(false));
    return () => controller.abort();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || isStreaming) return;
    const userMsg: ChatMessage = {
      id: uid(),
      role: 'user',
      content: text,
      timestamp: Date.now()
    };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setError(null);
    setIsStreaming(true);
    setHasUserInteracted(true);
    const assistantId = uid();
    streamingMsgIdRef.current = assistantId;
    const assistantMsg: ChatMessage = {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: Date.now()
    };
    setMessages(prev => [...prev, assistantMsg]);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      if (conversationId) {
        await sendMessage(conversationId, text, evt => {
          if (evt.event === 'init') return;
          try {
            const parsed = JSON.parse(evt.data);
            if (parsed.message) {
              setMessages(prev => prev.map(m => m.id === assistantId ? {
                ...m,
                content: m.content + parsed.message
              } : m));
            }
          } catch {/* ignore */}
        }, controller.signal);
      } else {
        const id = await initiateConversation(evt => {
          if (evt.event === 'init') return;
          try {
            const parsed = JSON.parse(evt.data);
            if (parsed.message) {
              setMessages(prev => prev.map(m => m.id === assistantId ? {
                ...m,
                content: m.content + parsed.message
              } : m));
            }
          } catch {/* ignore */}
        }, controller.signal);
        if (id) setConversationId(id);
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') return;
      const msg = 'Failed to get response. Please try again. Ensure "Workfront AdobeRM" org is selected. ';
      setError(msg);
      onError(msg);
      setMessages(prev => prev.filter(m => m.id !== assistantId));
    } finally {
      setIsStreaming(false);
      streamingMsgIdRef.current = null;
      abortRef.current = null;
      inputRef.current?.focus();
    }
  }, [input, isStreaming, conversationId, onError]);
  const handleClearChat = () => {
    clearWfHistory();
    setConversationId(null);
    setMessages([]);
    setError(null);
    setHasUserInteracted(false);
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  return <>
      {/* Messages */}
      <div className="ai-messages" data-id="element-728" data-mp="1wfj6zbn9nhs4">
        {isInitializing && <div style={{
        display: 'flex',
        justifyContent: 'center',
        padding: 20
      }} data-id="element-729" data-mp="1sf8zfujtsjb5">
            <Loader2 size={20} className="ai-spin" style={{
          color: '#7c3aed'
        }} data-id="element-730" data-mp="1nmxsi7qo3x93" />
          </div>}

        {messages.map(msg => <div key={msg.id} className={`ai-msg ai-msg-${msg.role}`} data-id="element-731" data-mp="1iont1kxwuaho">
            {msg.role === 'assistant' && <div className="ai-msg-avatar" data-id="element-732" data-mp="1dn6vnzemx65u">
                <Sparkles size={12} data-id="element-733" data-mp="18qgfzd6qscky" />
              </div>}
            <div className="ai-msg-bubble" data-id="element-734" data-mp="14oldi5d2rt5l">
              {msg.role === 'assistant' ? <div className="ai-md" data-id="element-735" data-mp="14qz9qhjhrbeq">
                  {msg.content ? <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
              a: ({
                children,
                ...props
              }) => <a {...props} target="_blank" rel="noopener noreferrer" data-id="element-737" data-mp="3pcwjj46im4cp">{children}</a>
            }} data-id="element-736" data-mp="3gboznr4qx625">{msg.content}</ReactMarkdown> : msg.id === streamingMsgIdRef.current ? <div className="ai-typing" data-id="element-738" data-mp="2wp0n80kuyegg">
                      <span data-id="element-739" data-mp="2wm4s0k0ea3vp" /><span data-id="element-740" data-mp="31n27upxxmlu1" /><span data-id="element-741" data-mp="31msrzv6lpv22" />
                    </div> : null}
                </div> : <span data-id="element-742" data-mp="36lvkyh9b4ato">{msg.content}</span>}
              {msg.role === 'assistant' && msg.content && <FeedbackBar messageId={msg.id} query={messages.find((m, i) => m.role === 'user' && i < messages.indexOf(msg) && messages[i + 1]?.id === msg.id)?.content} mode="workfront" data-id="element-743" data-mp="3bdng753er4q5" />}
            </div>
          </div>)}

        {error && <div className="ai-msg ai-msg-error" data-id="element-744" data-mp="1cb39ay52oc3y">
            <span data-id="element-745" data-mp="17b74iwojwgnc">{error}</span>
          </div>}

        {!isInitializing && !hasUserInteracted && !error && !input && <div className="ai-suggestions" data-id="element-746" data-mp="1lb87chodly5g">
            {WORKFRONT_SUGGESTIONS.map(s => <button key={s} className="ai-suggestion-chip" onClick={() => {
          setInput(s);
          inputRef.current?.focus();
        }} data-id="element-747" data-mp="1g9isss1y32cm">
                {s}
              </button>)}
          </div>}

        <div ref={messagesEndRef} data-id="element-748" data-mp="24293kxy43flb" />
      </div>

      {/* Input */}
      <div className="ai-input-bar" data-id="element-749" data-mp="1zzcrlq32uhqc">
        <textarea ref={inputRef} className="ai-input" placeholder="Ask anything about your deals..." value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown} rows={1} disabled={isStreaming} data-id="element-750" data-mp="20174ch4733jc" />
        <button className="ai-send-btn" onClick={() => void handleSend()} disabled={!input.trim() || isStreaming} title="Send message" data-id="element-751" data-mp="2xkdxx70rpsp5">
          {isStreaming ? <Loader2 size={16} className="ai-spin" data-id="element-752" data-mp="2nm7xyuyiuwmx" /> : <Send size={16} data-id="element-753" data-mp="2nkwvcygtbuxe" />}
        </button>
      </div>
    </>;
}
export function clearWorkfrontChatHistory(): void {
  clearWfHistory();
}